<!-- 
	This is the user profile page, it uses the dashboard layout in: 
	"./layouts/Dashboard.vue" .
 -->

<template>
	<div class="profile">

		<!-- Header -->
		<a-row :gutter="24" type="flex" class="mb-24">
			<a-col :span="24">
				<a-card :bordered="false" class="header-solid h-full">
					<template #title>
						<h5 class="font-semibold">Profile</h5>
						<p>Manage your account information and security settings</p>
					</template>
				</a-card>
			</a-col>
		</a-row>

		<a-row :gutter="24" type="flex">
			
			<!-- User Information Card -->
			<a-col :span="24" :md="12" class="mb-24">
				<a-card :bordered="false" title="Account Information" :loading="loading">
					<a-spin :spinning="loading">
						<div v-if="userInfo" class="user-info">
							<div class="user-avatar-section">
								<div class="user-basic-info">
									<h3 class="user-name">{{ userInfo.firstName }} {{ userInfo.lastName }}</h3>
									<p class="user-username">{{ userInfo.email }}</p>
								</div>
							</div>

							<a-divider />

							<div class="user-details">
								<div class="detail-item">
									<div class="detail-label">Email</div>
									<div class="detail-value">{{ userInfo.email }}</div>
								</div>
								<div class="detail-item">
									<div class="detail-label">First Name</div>
									<div class="detail-value">{{ userInfo.firstName }}</div>
								</div>
								<div class="detail-item">
									<div class="detail-label">Last Name</div>
									<div class="detail-value">{{ userInfo.lastName }}</div>
								</div>
								<div class="detail-item">
									<div class="detail-label">Organizations</div>
									<div class="detail-value">
										<div v-if="userInfo.organizations && userInfo.organizations.length > 0" class="organizations-list">
											<div v-for="org in userInfo.organizations" :key="org.id" class="org-item">
												<div class="org-info">
													<span class="org-name">{{ org.name }}</span>
													<div class="org-tags">
														<a-tooltip :title="getRoleDescription(org.userRole)">
															<a-tag :color="getRoleColor(org.userRole)">
																{{ org.userRole }}
															</a-tag>
														</a-tooltip>
														<a-tag :color="org.enabled ? 'green' : 'red'">
															{{ org.enabled ? 'Active' : 'Disabled' }}
														</a-tag>
													</div>
												</div>
											</div>
										</div>
										<span v-else style="color: #8c8c8c;">No organizations</span>
									</div>
								</div>
							</div>

							<a-divider />

							<div class="user-actions">
								<a-button type="primary" @click="refreshUserInfo" :loading="loading">
									<a-icon type="reload" />
									Refresh Information
								</a-button>
							</div>
						</div>
						<div v-else-if="!loading" class="user-info-error">
							<a-result status="warning" title="Unable to load user information">
								<template #extra>
									<a-button type="primary" @click="fetchUserInfo">
										<a-icon type="reload" />
										Retry
									</a-button>
								</template>
							</a-result>
						</div>
					</a-spin>
				</a-card>
			</a-col>

			<!-- Change Password Card -->
			<a-col :span="24" :md="12" class="mb-24">
				<a-card :bordered="false" title="Change Password">
					<a-form :form="passwordForm" layout="vertical" @submit.prevent="changePassword">
						<a-form-item label="Current Password">
							<a-input-password 
								v-decorator="['currentPassword', { rules: [{ required: true, message: 'Please enter your current password' }] }]"
								placeholder="Enter current password"
							/>
						</a-form-item>
						
						<a-form-item label="New Password">
							<a-input-password 
								v-decorator="['newPassword', { rules: [
									{ required: true, message: 'Please enter a new password' },
									{ min: 6, message: 'Password must be at least 6 characters long' }
								] }]"
								placeholder="Enter new password"
							/>
						</a-form-item>
						
						<a-form-item label="Confirm New Password">
							<a-input-password 
								v-decorator="['confirmPassword', { rules: [
									{ required: true, message: 'Please confirm your new password' },
									{ validator: validateConfirmPassword }
								] }]"
								placeholder="Confirm new password"
							/>
						</a-form-item>

						<a-form-item>
							<a-button type="primary" html-type="submit" :loading="changingPassword" block>
								<a-icon type="lock" />
								Change Password
							</a-button>
						</a-form-item>
					</a-form>

					<a-divider />

					<div class="security-tips">
						<h4>Security Tips</h4>
						<ul>
							<li>Use a strong password with at least 8 characters</li>
							<li>Include uppercase, lowercase, numbers, and symbols</li>
							<li>Don't reuse passwords from other accounts</li>
							<li>Change your password regularly</li>
						</ul>
					</div>
				</a-card>
			</a-col>

		</a-row>

	</div>
</template>

<script>
	import { authFetch } from '../auth';

	export default {
		data() {
			return {
				userInfo: null,
				loading: false,
				changingPassword: false,
			}
		},
		beforeCreate() {
			this.passwordForm = this.$form.createForm(this, { name: 'changePassword' });
		},
		mounted() {
			this.fetchUserInfo();
		},
		methods: {
			async fetchUserInfo() {
				this.loading = true;
				try {
					const response = await authFetch(`${process.env.VUE_APP_API_BASE_URL}/api/auth/me`);
					
					if (response.ok) {
						this.userInfo = await response.json();
            console.log(this.userInfo);
					} else {
						this.$message.error('Failed to load user information');
					}
				} catch (e) {
					console.error('fetchUserInfo error', e);
					this.$message.error('Error loading user information');
				} finally {
					this.loading = false;
				}
			},
			
			refreshUserInfo() {
				this.fetchUserInfo();
			},

			getRoleColor(role) {
				const colors = {
					'ADMIN': 'red',
					'OPERATOR': 'blue',
					'VIEWER': 'green'
				};
				return colors[role] || 'default';
			},

			getRoleDescription(role) {
				const descriptions = {
					'ADMIN': 'Administrator - Full access to all features and settings',
					'OPERATOR': 'Operator - Standard access to manage transactions and data',
					'VIEWER': 'Viewer - Read-only access to view data'
				};
				return descriptions[role] || role;
			},

			validateConfirmPassword(rule, value, callback) {
				const form = this.passwordForm;
				if (value && value !== form.getFieldValue('newPassword')) {
					callback('Passwords do not match');
				} else {
					callback();
				}
			},

			async changePassword() {
				this.passwordForm.validateFields(async (err, values) => {
					if (!err) {
						this.changingPassword = true;
						try {
							const response = await authFetch(`${process.env.VUE_APP_API_BASE_URL}/api/auth/change-password`, {
								method: 'POST',
								body: JSON.stringify({
									currentPassword: values.currentPassword,
									newPassword: values.newPassword
								})
							});
							
							if (response.ok) {
								this.$message.success('Password changed successfully');
								this.passwordForm.resetFields();
							} else if (response.status === 401) {
								this.$message.error('Current password is incorrect');
							} else {
								const errorData = await response.text();
								this.$message.error(errorData || 'Failed to change password');
							}
						} catch (e) {
							console.error('changePassword error', e);
							this.$message.error('Error changing password');
						} finally {
							this.changingPassword = false;
						}
					}
				});
			},
		},
		name: 'Profile',
	}
</script>

<style lang="scss">
.profile {
	.user-info {
		.user-avatar-section {
			display: flex;
			align-items: center;
			gap: 16px;
			margin-bottom: 16px;

			.user-basic-info {
				.user-name {
					margin: 0 0 4px 0;
					font-size: 24px;
					font-weight: 600;
				}

				.user-username {
					margin: 0 0 8px 0;
					color: #8c8c8c;
					font-size: 14px;
				}

				.user-roles {
					display: flex;
					gap: 4px;
				}
			}
		}

		.user-details {
			.detail-item {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 8px 0;
				border-bottom: 1px solid #f0f0f0;

				&:last-child {
					border-bottom: none;
				}

				.detail-label {
					font-weight: 500;
					color: #595959;
				}

				.detail-value {
					color: #262626;

					.organizations-list {
						display: flex;
						flex-direction: column;
						gap: 12px;

						.org-item {
							padding: 8px 0;
							border-bottom: 1px solid #f0f0f0;

							&:last-child {
								border-bottom: none;
							}

							.org-info {
								display: flex;
								justify-content: space-between;
								align-items: center;
								flex-wrap: wrap;
								gap: 8px;

								.org-name {
									font-weight: 500;
									flex: 1;
									min-width: 120px;
								}

								.org-tags {
									display: flex;
									gap: 4px;
									flex-wrap: wrap;
								}
							}
						}
					}
				}
			}
		}

		.user-actions {
			text-align: center;
		}
	}

	.user-info-error {
		text-align: center;
	}

	.security-tips {
		h4 {
			margin-bottom: 8px;
			font-size: 14px;
			font-weight: 600;
		}

		ul {
			margin: 0;
			padding-left: 16px;
			color: #8c8c8c;
			font-size: 13px;

			li {
				margin-bottom: 4px;
			}
		}
	}
}

/* Mobile responsiveness */
@media (max-width: 768px) {
	.profile {
		.user-info {
			.user-avatar-section {
				flex-direction: column;
				text-align: center;
				
				.user-basic-info {
					text-align: center;
				}
			}

			.user-details {
				.detail-item {
					flex-direction: column;
					align-items: flex-start;
					gap: 4px;

					.detail-label {
						font-size: 12px;
						text-transform: uppercase;
					}
				}
			}
		}
	}
}
</style>